import type { CastInProgress } from '../state/store';

/**
 * What the cast bar shows: how far through its window the local player's cast is, and how long is
 * left. Pure, so the rules are testable without a DOM or a clock.
 *
 * Asked for directly (2026-09-27): *we need a cast bar somewhere to know we are casting something
 * and when the cast will end.* Before it, the stretched animation and the `CASTING` state were the
 * only readouts a caster had (`STATUS.md`).
 */
export interface CastBarView {
  name: string;
  /** 0 → 1 through the cast window. */
  progress: number;
  /** Seconds left, never negative. */
  remaining: number;
}

/**
 * How long the snapshot may still say something other than `CASTING` after a cast began before the
 * bar believes it. The start event and the state change arrive in the same tick from the server,
 * but the snapshot the HUD holds can be a frame behind the event, and a bar that vanished on its
 * first frame would be a flicker. One snapshot interval at 30 Hz, and some.
 */
export const CAST_STATE_GRACE_MS = 120;

/**
 * The bar, or null when there is nothing to show.
 *
 * - **No cast recorded** → nothing.
 * - **The window has run out** → nothing, even if the resolution has not arrived yet: a bar held
 *   full while the server resolves would read as the cast being stuck.
 * - **The player is no longer casting** (past the grace) → nothing: a move, an attack, a roll or
 *   death cancels a cast without an event of its own, and the snapshot state is how that shows.
 */
export function castBarView(cast: CastInProgress | null, selfState: string | undefined, now: number): CastBarView | null {
  if (!cast || cast.seconds <= 0) return null;
  const elapsed = (now - cast.startedAt) / 1000;
  if (elapsed >= cast.seconds) return null;
  if (selfState !== 'CASTING' && elapsed * 1000 > CAST_STATE_GRACE_MS) return null;
  const progress = Math.min(1, Math.max(0, elapsed / cast.seconds));
  return { name: cast.name, progress, remaining: Math.max(0, cast.seconds - elapsed) };
}

/**
 * The town portal's opening, as a bar: `remaining` is the snapshot's `portalCastSec`, and `total` is
 * the first value this opening was seen with — **the length is never copied into the client**
 * (`PortalCastSec` is server config; a copy here would drift, the `worldCentre`/`tileCentre` fault).
 * Asked for directly (2026-09-27): *town portal should display a casting bar.* Being hit cancels an
 * opening, and the field then goes absent, which is what makes the bar go.
 */
export function portalBarView(remaining: number | undefined, total: number): CastBarView | null {
  if (remaining === undefined || !(remaining > 0) || !(total > 0)) return null;
  return { name: 'Town Portal', progress: Math.min(1, Math.max(0, 1 - remaining / total)), remaining };
}
